import { Component, OnInit, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { PlanService, Plan, PlanResponse } from '../services/plan.service';

@Component({
  selector: 'app-plan',
  templateUrl: './plan.component.html',
  styleUrls: ['./plan.component.scss']
})
export class PlanComponent implements OnInit, OnChanges {
  @Input() standortId?: string;
  @Input() sektionId?: string;
  @Input() clientId?: string;
  @Input() isEmbedded: boolean = false;
  @Input() parentContext?: string;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = ['bezeichnung', 'sektion', 'upload', 'active', 'created_at', 'actions'];
  dataSource: MatTableDataSource<Plan> = new MatTableDataSource<Plan>([]);
  isLoadingResults = true;
  totalItems = 0;
  currentPage = 1;
  pageSize = 25;
  searchTerm = '';
  searchValue = '';

  constructor(
    private planService: PlanService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.loadPlans();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['standortId'] || changes['sektionId'] || changes['clientId']) {
      this.loadPlans();
    }
  }

  loadPlans(): void {
    this.isLoadingResults = true;
    
    let observable;
    
    if (this.sektionId) {
      observable = this.planService.getPlansBySektion(this.sektionId, this.currentPage, this.pageSize);
    } else if (this.standortId) {
      observable = this.planService.getPlansByStandort(this.standortId, this.currentPage, this.pageSize);
    } else if (this.clientId) {
      observable = this.planService.getPlansByClient(this.clientId, this.currentPage, this.pageSize);
    } else {
      observable = this.planService.getPlans(this.currentPage, this.pageSize, this.searchTerm);
    }

    observable.subscribe({
      next: (response: PlanResponse) => {
        this.dataSource.data = response.data;
        this.totalItems = response.total;
        this.isLoadingResults = false;
      },
      error: (error) => {
        console.error('Error loading plans:', error);
        this.snackBar.open('Fehler beim Laden der Pläne', 'Schließen', { duration: 3000 });
        this.isLoadingResults = false;
      }
    });
  }

  onPlanClick(plan: Plan): void {
    if (!plan.id) {
      console.error('❌ Plan ID is missing or invalid:', plan.id);
      this.snackBar.open('Plan ID fehlt - kann nicht geöffnet werden', 'Schließen', { duration: 3000 });
      return;
    }
    
    // Navigate to standalone plan-edit route
    this.router.navigate(['/plan', plan.id], { 
      queryParams: { 
        standortId: this.standortId
      } 
    });
  }

  createPlan(): void {
    if (this.isEmbedded) {
      // In embedded mode, navigate to plan-edit with create mode
      this.router.navigate(['/plan', 'new'], { 
        queryParams: { 
          sektionId: this.sektionId,
          standortId: this.standortId,
          clientId: this.clientId,
          mode: 'create'
        }
      });
    } else {
      // In standalone mode, navigate to plan-edit with create mode
      this.router.navigate(['/plan', 'new'], { queryParams: { mode: 'create' } });
    }
  }

  editPlan(plan: Plan): void {
    this.router.navigate(['/plan', plan.id, 'edit']);
  }

  /** Open the Konva-based plan-draw component to draw a brand-new plan. */
  drawPlan(): void {
    this.router.navigate(['/plan', 'draw'], {
      queryParams: {
        sektionId: this.sektionId,
        standortId: this.standortId
      }
    });
  }

  /** Open the plan-draw component in edit mode for an existing plan. */
  drawEditPlan(plan: Plan): void {
    this.router.navigate(['/plan', 'draw', plan.id], {
      queryParams: { standortId: this.standortId || plan.standort_id }
    });
  }

  deletePlan(plan: Plan): void {
    if (confirm(`Möchten Sie den Plan "${plan.bezeichnung}" wirklich löschen?`)) {
      this.planService.deletePlan(plan.id).subscribe({
        next: () => {
          this.snackBar.open('Plan erfolgreich gelöscht', 'Schließen', { duration: 3000 });
          this.loadPlans();
        },
        error: (error) => {
          console.error('Error deleting plan:', error);
          this.snackBar.open('Fehler beim Löschen des Plans', 'Schließen', { duration: 3000 });
        }
      });
    }
  }

  toggleActive(plan: Plan): void {
    const updatedPlan = { ...plan, active: !plan.active };
    this.planService.updatePlan(plan.id, updatedPlan).subscribe({
      next: (response) => {
        // Update the plan in the data source
        const index = this.dataSource.data.findIndex(p => p.id === plan.id);
        if (index !== -1) {
          this.dataSource.data[index] = response;
          this.dataSource._updateChangeSubscription();
        }
        this.snackBar.open('Plan Status aktualisiert', 'Schließen', { duration: 2000 });
      },
      error: (error) => {
        console.error('Error updating plan:', error);
        this.snackBar.open('Fehler beim Aktualisieren des Plans', 'Schließen', { duration: 3000 });
      }
    });
  }

  onPageChange(event: any): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadPlans();
  }

  onSearch(event: any): void {
    this.searchTerm = event.target.value;
    this.searchValue = event.target.value;
    this.currentPage = 1;
    this.loadPlans();
  }

  getDisplayCategory(mimeType: string): string {
    if (mimeType?.startsWith('image/')) {
      return 'Bild';
    } else if (mimeType === 'application/pdf') {
      return 'PDF';
    } else if (mimeType?.includes('word') || mimeType?.includes('document')) {
      return 'Dokument';
    } else if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) {
      return 'Kalkulation';
    } else {
      return 'Dokument';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getStandortNames(): string[] {
    return [];
  }

  getStatusClasses(active: boolean): string {
    if (active) {
      return 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:ring-green-400/20';
    } else {
      return 'bg-gray-50 text-gray-600 ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20';
    }
  }

  getActivationStatusClasses(isActive: boolean): string {
    if (isActive) {
      return 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/20';
    } else {
      return 'bg-gray-50 text-gray-600 ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20';
    }
  }

  activatePlan(plan: Plan): void {
    if (!plan.id) {
      this.snackBar.open('Plan ID nicht gefunden', 'Schließen', { duration: 3000 });
      return;
    }

    this.planService.activatePlan(plan.id).subscribe({
      next: (response) => {
        this.snackBar.open('Plan erfolgreich aktiviert', 'Schließen', { duration: 3000 });
        this.loadPlans(); // Reload to update the table
      },
      error: (error) => {
        console.error('Error activating plan:', error);
        const errorMessage = error.error?.message || 'Fehler beim Aktivieren des Plans';
        this.snackBar.open(errorMessage, 'Schließen', { duration: 5000 });
      }
    });
  }

  deactivatePlan(plan: Plan): void {
    if (!plan.id) {
      this.snackBar.open('Plan ID nicht gefunden', 'Schließen', { duration: 3000 });
      return;
    }

    this.planService.deactivatePlan(plan.id).subscribe({
      next: (response) => {
        this.snackBar.open('Plan erfolgreich deaktiviert', 'Schließen', { duration: 3000 });
        this.loadPlans(); // Reload to update the table
      },
      error: (error) => {
        console.error('Error deactivating plan:', error);
        const errorMessage = error.error?.message || 'Fehler beim Deaktivieren des Plans';
        this.snackBar.open(errorMessage, 'Schließen', { duration: 5000 });
      }
    });
  }
}
